import React from "react";
import type { UseMutationResult } from "@tanstack/react-query";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyMutation = UseMutationResult<any, unknown, any, any>;

function getMutationErrorMessage(err: unknown): string {
  if (err && typeof err === "object" && "response" in err) {
    const data = (err as { response?: { data?: { error?: string; detail?: string } } }).response
      ?.data;
    if (data?.error) return String(data.error);
    if (data?.detail) return String(data.detail);
  }
  if (err instanceof Error) return err.message;
  return "Something went wrong";
}

interface MutationErrorAlertProps {
  mutations: AnyMutation[];
}

/**
 * Surfaces the first error from a list of mutations as a dismissible alert.
 * Dismiss resets every mutation in the array.
 */
const MutationErrorAlert: React.FC<MutationErrorAlertProps> = ({ mutations }) => {
  const errored = mutations.find((m) => m.error);
  if (!errored) return null;

  return (
    <Alert variant="destructive" className="relative mb-5 pr-10">
      <AlertDescription>{getMutationErrorMessage(errored.error)}</AlertDescription>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="absolute right-2 top-2 h-7"
        onClick={() => mutations.forEach((m) => m.reset())}
      >
        Dismiss
      </Button>
    </Alert>
  );
};

export default MutationErrorAlert;
